import type { ApiError, ApiErrorResponse } from "@/types/common.types";
import { notifications } from "@mantine/notifications";
import { Alert, type AlertOptions } from "@onengine/component-library";
import styles from "@/styles/useAlert.module.scss";

// used in axiosInstance.ts (an axios interceptor), which runs outside React component/hook context.
// Exported (SERV-3138) so a caller that needs a toast action button (AlertOptions.actions,
// e.g. the alerts page's "View" link on a realtime alert.created toast) isn't limited to the
// action-less wrapper functions below (showInfoAlert etc.).
export const showAlert = ({
  id,
  title,
  message,
  variant = "strong",
  size = "base",
  type = "info",
  actions,
  onClose,
  autoClose = 3000,
}: Omit<AlertOptions, "message"> & { message?: string; autoClose?: number | false }) => {
  const notifId = id ?? `alert-${title}-${message ?? ""}`;

  const handleClose = () => {
    notifications.hide(notifId);
    onClose?.();
  };

  notifications.show({
    id: notifId,
    withCloseButton: false,
    classNames: {
      root: styles.notificationWrapper,
    },
    message: (
      <Alert
        title={title}
        message={message}
        type={type}
        variant={variant}
        size={size}
        presentation="toast"
        actions={actions}
        closable
        onClose={handleClose}
      />
    ),
    autoClose,
  });
};

const FALLBACK_ERROR_MESSAGE = "Something went wrong, please try again later.";

const ERROR_CODE_TITLES: Record<string, string> = {
  VALIDATION_ERROR: "Validation Error",
  RESOURCE_NOT_FOUND: "Not Found",
  DATABASE_ERROR: "Database Error",
  FORBIDDEN: "Forbidden",
  UNAUTHORIZED: "Unauthorized",
  SESSION_EXPIRED: "Session Expired",
  INVALID_SESSION: "Invalid Session",
  SESSION_REVOKED: "Session Revoked",
  INTERNAL_SERVER_ERROR: "Server Error",
  UNIQUE_CONSTRAINT_VIOLATION: "Conflict",
  DEPENDENT_RECORDS_EXIST: "Conflict",
  FOREIGN_KEY_VIOLATION: "Invalid Reference",
  NULL_CONSTRAINT_VIOLATION: "Validation Error",
  DATABASE_CONNECTION_ERROR: "Service Unavailable",
};

function getErrorTitle(status?: number, code?: string): string {
  if (code && ERROR_CODE_TITLES[code]) {
    return ERROR_CODE_TITLES[code];
  }
  switch (status) {
    case 400:
      return "Validation Error";
    case 403:
      return "Forbidden";
    case 404:
      return "Not Found";
    case 409:
      return "Conflict";
    case 500:
      return "Server Error";
    case 503:
      return "Service Unavailable";
    default:
      return "Error";
  }
}

// Backend zod schemas without custom messages leak raw phrasings like
// "Invalid input: expected string, received undefined" (SERV-2472 item 10) — or the
// bare "Invalid input" that unions/literals emit for an unanswered field, e.g. the
// rates step's rateLogic radio (SERV-2469 follow-up).
export function humanizeValidationMessage(message: string): string {
  if (
    /^Invalid input$/i.test(message) ||
    /^Invalid input: expected .+, received (undefined|null)$/i.test(message)
  ) {
    return "This field is required.";
  }
  return message;
}

// Some endpoints return validation details as plain strings rather than
// { field, message } objects — coerce both shapes to a display string.
function detailMessage(detail: unknown): string | undefined {
  if (typeof detail === "string") return humanizeValidationMessage(detail);
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return humanizeValidationMessage(message);
  }
  return undefined;
}

function extractPayloadErrorMessage(errorData: unknown): string | undefined {
  if (!errorData) return undefined;

  // Plain-string bodies are only useful when they aren't markup (gateway HTML pages)
  if (typeof errorData === "string") {
    const trimmed = errorData.trim();
    return trimmed && !trimmed.startsWith("<") ? trimmed : undefined;
  }

  const data = errorData as ApiErrorResponse & { message?: string };

  const details = data.error?.details;
  if (Array.isArray(details) && details.length > 0) {
    const messages = details.map(detailMessage).filter(Boolean);
    if (messages.length > 0) return messages.join(" ");
  }

  if (data.error?.message) return data.error.message;
  if (typeof data.message === "string" && data.message) return data.message;

  return undefined;
}

export function getApiErrorMessage(
  error: unknown,
  fallbackMessage = FALLBACK_ERROR_MESSAGE,
): string {
  const apiError = error as ApiError;

  return (
    extractPayloadErrorMessage(apiError?.response?.data) ??
    // No usable payload — for inline surfaces the transport error ("Network Error",
    // "Request failed with status code 413") is more actionable than a generic fallback
    apiError?.message ??
    fallbackMessage
  );
}

export function handleApiError(error: ApiError, fallbackMessage = FALLBACK_ERROR_MESSAGE): void {
  const status = error.response?.status;

  // 401s are handled by the axios interceptor (token refresh / redirect) — no alert needed
  if (status === 401) return;

  // 409s are handled by the axios interceptor — skip to avoid duplicate alerts
  if (status === 409) return;
  const errorData = error.response?.data;

  if (!errorData) {
    // Toast surfaces keep the caller's friendly fallback — raw transport strings
    // are reserved for inline surfaces that opt in via getApiErrorMessage
    showAlert({ title: "Error", message: fallbackMessage, type: "danger" });
    return;
  }

  const { code, details } = errorData.error ?? {};

  if (Array.isArray(details) && details.length > 0) {
    details.forEach((detail) => {
      showAlert({
        title: "Validation Error",
        message: detailMessage(detail) || fallbackMessage,
        type: "danger",
      });
    });
    return;
  }

  showAlert({
    title: getErrorTitle(status, code),
    message: extractPayloadErrorMessage(errorData) ?? fallbackMessage,
    type: "danger",
  });
}

export function handleApiSuccess(message: string, title = "Success"): void {
  showAlert({ title, message, type: "success" });
}

export function showErrorAlert(message: string, title = "Error"): void {
  showAlert({ title, message, type: "danger" });
}

export function showWarningAlert(
  message: string,
  title = "Warning",
  autoClose?: number | false,
): void {
  showAlert({ title, message, type: "warning", autoClose });
}

export function showInfoAlert(message: string, title = "Info"): void {
  showAlert({ title, message, type: "info" });
}
